import {pageUrlMap} from '@jobseeker/common-core/constants';
// Direct subpath, not the utils barrel: analytics is imported by _app (via
// botGuard); the barrel re-exports authHelper -> crypto-js. deferTask is
// crypto-free.
import {deferTask} from '@jobseeker/common-core/utils/common';
import {EventProps, gaTrackCustomEvent} from './gtag';
import {clarityEvent, claritySetTag, clarityIdentifyUser} from './clarity';
import ubaEventLogger from './ubaEventLogger';

export interface TrackingEvent extends EventProps {}

export const InitiateUBA = (pageName: string) => {
  pageName &&
    (window as any)?.tracking &&
    (window as any)?.tracking.init(
      {
        appId: process.env.NEXT_PUBLIC_UBA_APP_ID,
      },
      {
        pageName: pageName,
      }
    );
};

const validateSessionUsername = (raw: unknown): string => {
  if (raw == null) return '';
  if (typeof raw === 'number') {
    return raw > 0 ? String(raw) : '';
  }
  if (typeof raw === 'string') {
    const parsed = Number(raw);
    if (!isNaN(parsed) && parsed > 0) {
      return String(parsed);
    }
  }
  return '';
};

export const TrackUbaAudit = (
  eventName: string,
  keyNamesObject: Record<string, unknown>
) => {
  try {
    ubaEventLogger.logEvent(eventName, keyNamesObject);
  } catch (e) {
    console.log('UBA Audit Logger Error', e);
  }
};

export interface TrackEventUBAOptions {
  immediate?: boolean;
}

export const TrackEventUBA = (
  eventName: string,
  actionType: string,
  obj: Record<string, unknown>,
  options: TrackEventUBAOptions = {}
) => {
  console.log('TrackEventUBA', 'TrackingEvent', 'beforeDoTracking', {
    eventName,
    tracking: (window as any)?.tracking,
  });
  if (typeof window === 'undefined') return;
  if (!(window as any)?.tracking) return;
  const keyNamesObject = {
    actionSrc: 'normal',
    actionType,
    ref: new URL(window.location.href).searchParams.get('ref') || '',
    ...obj,
    sessionUsername:
      validateSessionUsername(sessionStorage.getItem('userId')) ||
      validateSessionUsername(obj.sessionUsername) ||
      '',
  };
  (window as any)?.tracking.doTracking({
    uba: {eventName, keyNames: keyNamesObject},
  });
  clarityIdentifyUser();
  clarityEvent(eventName);
  if (typeof obj.regStep === 'string') {
    claritySetTag('regStep', obj.regStep);
  }
  if (typeof obj.field_name === 'string') {
    claritySetTag('field_name', obj.field_name);
  }
  if (actionType) {
    claritySetTag('actionType', actionType);
  }
  const queueUbaEvent = () => {
    try {
      ubaEventLogger.logEvent(eventName, keyNamesObject);
    } catch (e) {
      console.log('UBA Logger Error', e);
    }
  };
  if (options.immediate) {
    queueUbaEvent();
  } else {
    deferTask(queueUbaEvent);
  }
  console.log('TrackingEvent', {
    eventName,
    keyNamesObject,
    trackingObj: (window as any)?.tracking,
    doTrackingObj: (window as any)?.tracking.doTracking,
  });
};

export const handleTracking = (
  action: string,
  tracking: Partial<TrackingEvent>,
  actiontype?: string,
  isUba: boolean = false
) => {
  if (typeof window === 'undefined') return;
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  gaTrackCustomEvent(isAppIimjobs ? action : `${action}New`, tracking);
  if (isUba) {
    TrackEventUBA(action, actiontype || '', tracking);
  }
};

export const getUbaPageName = () => {
  if (typeof window === 'undefined') return '';

  const path = window.location.pathname || '';

  if (path === '') return '';

  let pageName = '';
  if (path === '/') {
    pageName = 'Home Page';
  } else {
    Object.entries(pageUrlMap).map(([key]) => {
      if (path.includes(key)) {
        pageName = pageUrlMap[key];
      }
    });
  }
  return pageName;
};

export type FieldValueActionType =
  | 'click'
  | 'upload'
  | 'focus-in'
  | 'focus-out'
  | 'auto-fill';

export type FieldValueSource =
  | 'resume parser'
  | 'user filled'
  | 'browser-autofill';

export interface FieldValueLevelTrackingParams {
  actionType: FieldValueActionType;
  regStep: string;
  field_name: string;
  field_value: string;
  field_value_source?: FieldValueSource;
  required: 0 | 1;
  isValid: 0 | 1;
  validation_error: string;
}

export const trackFieldValueLevel = (params: FieldValueLevelTrackingParams) => {
  TrackEventUBA('fieldValueLevelTracking', params.actionType, {
    regStep: params.regStep,
    field_name: params.field_name,
    field_value: params.field_value,
    field_value_source: params.field_value_source ?? 'user filled',
    required: params.required,
    isValid: params.isValid,
    validation_error: params.validation_error,
  });
};

export const getUbaSource = (query: any) => {
  const pageIdQuery = String(query?.pageid || '');
  const pageIdParam: Record<string, string> = {
    jd: 'Job Detail',
    jf: 'Jobfeed',
  };
  const resolvedPageId = pageIdParam[pageIdQuery.toLowerCase()] || pageIdQuery;

  const refFeatureQuery = String(query?.reffeature || '');
  const refFeatureMap: Record<string, string> = {
    '2': 'Followup',
    '3': 'Insights',
  };
  const formattedFeature =
    refFeatureMap[refFeatureQuery] ||
    (refFeatureQuery
      ? refFeatureQuery.charAt(0).toUpperCase() +
        refFeatureQuery.slice(1).toLowerCase()
      : '');
  const pageSuffix =
    pageIdQuery.toLowerCase() === 'jd'
      ? 'Job Description'
      : pageIdQuery.toLowerCase() === 'jf'
        ? 'Jobfeed'
        : '';

  return formattedFeature && pageIdQuery
    ? `${formattedFeature} - ${pageSuffix}`
    : (query?.source as string) || resolvedPageId || '';
};
